import React from 'react'
import { GoogleForJobs } from '../offers/offer/googleForJobs'

type JobPostingProps = {
  data: GoogleForJobs | null
}

export const JobPosting: React.FC<JobPostingProps> = ({ data }) => {
  if (!data) {
    return null
  }

  // Format description en HTML si nécessaire
  const formatDescription = (description: string | null): string => {
    if (!description) return ''
    // Si la description n'est pas déjà en HTML, l'encapsuler dans des balises p
    if (!description.includes('<p>') && !description.includes('<ul>')) {
      return `<p>${description}</p>`
    }
    return description
  }

  // Déterminer si c'est un poste en télétravail
  const isRemoteJob = data.fullyRemoteJob === 'Full remote'

  const jobStructuredData = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: data?.title,
    description: formatDescription(data?.description),
    identifier: {
      '@type': 'PropertyValue',
      name: 'Strapi',
      value: data?.id,
    },
    datePosted: data?.publishedDate || data.createdAt || data.updatedAt,
    // Ajouter validThrough si disponible (obligatoire selon Google)
    // ...(data?.validThrough && { validThrough: data.validThrough }),
    employmentType: data?.contractType,
    hiringOrganization: {
      '@type': 'Organization',
      name: data?.firmName,
      sameAs: data?.sameAs,
      ...(data?.firmLogo && { logo: data?.firmLogo }),
    },
    // Gestion du télétravail
    ...(isRemoteJob && {
      jobLocationType: 'TELECOMMUTE',
      applicantLocationRequirements: {
        '@type': 'Country',
        name: 'France',
      },
    }),
    // Lieu physique seulement si pas en télétravail complet
    ...(!isRemoteJob && {
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: data?.street,
          addressLocality: data?.city,
          postalCode: data?.postalCode,
          addressCountry: 'FR',
        },
      },
    }),
    // Salaire seulement si disponible
    ...(data?.salarymin && data?.salarymax && {
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: 'EUR',
        value: {
          '@type': 'QuantitativeValue',
          currency: 'EUR',
          minValue: data?.salarymin,
          maxValue: data?.salarymax,
          unitText: 'YEAR',
        },
      },
    }),
    // Propriétés optionnelles pour améliorer la visibilité
    ...(data?.directApply !== undefined && { directApply: data.directApply }),
    ...(data?.educationRequirements && { educationRequirements: data.educationRequirements }),
    ...(data?.experienceRequirements && { experienceRequirements: data.experienceRequirements }),
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(jobStructuredData),
      }}
    />
  )
}
